import type { V2 } from '../../core/math';
import { Rng } from '../../core/rng';
import { fbm2, scatter } from '../../zones/build';
import { type Coast, type LandProfile, type Pier, bearing, floatY, landHeight, layPier, pierTip, shoreDistance, shorePoint, shoreRadius, withPier } from '../../zones/IslandKit';
import type { PortalDef, ZoneLayout } from '../../zones/types';
import { ZoneBuilder } from '../../zones/ZoneBuilder';

const WATER = 0;
const DECK = WATER + 0.5;

interface Isle extends Coast {
  id: string;
  peak: number;
}

/** Islands of the Gleaming Sea (terrain blobs; each also blocks ships). */
export const SEA_ISLES: readonly Isle[] = [
  { id: 'havenreach', x: -250, z: 220, r: 95, peak: 9, waves: [[3, 0.1, 0.3], [5, 0.06, 1.9]] },
  { id: 'haven', x: -150, z: -170, r: 40, peak: 6, waves: [[3, 0.13, 0.7], [5, 0.087, -1.1]] },
  { id: 'glimmer', x: 185, z: -55, r: 58, peak: 12, waves: [[2, 0.14, 0.4], [3, 0.1, -1.2], [7, 0.055, 0.5]] },
  { id: 'islet_a', x: 55, z: 150, r: 13, peak: 5, waves: [[3, 0.15, 1.1]] },
  { id: 'islet_b', x: -25, z: -45, r: 9, peak: 6, waves: [[3, 0.15, 2.3]] },
  { id: 'islet_c', x: 225, z: 205, r: 17, peak: 5, waves: [[4, 0.12, 0.2]] },
  { id: 'islet_d', x: -255, z: -15, r: 11, peak: 4, waves: [[3, 0.14, -0.7]] },
  { id: 'islet_e', x: 95, z: -230, r: 15, peak: 6, waves: [[5, 0.1, 1.6]] },
];

/**
 * Sea hazards (centres are also spawn points `storm_*` / `fog_*`): storms
 * batter the hull, fog banks hide the way. The sailing controller reads the
 * radii from here.
 */
export const SEA_HAZARDS: readonly { id: string; kind: 'storm' | 'fog'; x: number; z: number; r: number }[] = [
  { id: 'storm_0', kind: 'storm', x: -10, z: 55, r: 45 },
  { id: 'storm_1', kind: 'storm', x: 40, z: -130, r: 40 },
  { id: 'fog_0', kind: 'fog', x: -185, z: 10, r: 55 },
  { id: 'fog_1', kind: 'fog', x: 150, z: 150, r: 60 },
];

/** Portal radius at the docks (ships are big). */
const DOCK_RADIUS = 6;

function isle(id: string): Isle {
  const i = SEA_ISLES.find((x) => x.id === id);
  if (!i) throw new Error(`no isle ${id}`);
  return i;
}

function profile(i: Isle): LandProfile {
  return { seaFloor: -9, shore: 0.4, rise: i.peak, inland: i.r * 0.7, beach: 5, hills: Math.min(2.5, i.peak * 0.3), seed: 3200 + Math.round(i.r) };
}

/** A pier on the side of `i` facing `toward`, with its dock portal just past the tip. */
function dockOf(i: Isle, toward: V2, length = 24): { pier: Pier; portalAt: V2; spawnAt: V2 } {
  const a = Math.atan2(toward.x - i.x, toward.z - i.z);
  const from = shorePoint(i, a, -6);
  const pier: Pier = { from, dir: a, length, halfWidth: 1.8 };
  return { pier, portalAt: pierTip(pier, 7), spawnAt: pierTip(pier, 30) };
}

/**
 * THE GLEAMING SEA (sailing). Open water (terrain far below a water sheet)
 * dotted with islands raised from the sea floor: the Havenreach harbour
 * headland (south-west), Haven Isle (north-west), Glimmer Isle (east) and
 * five rocky islets. The ship moves on the regular nav grid: the water sheet
 * is marked shallow (`deep: false`) so all water is navigable, while island
 * polygons and piers are blockers. Dock portals sit in open water just past
 * each pier (to `havenreach`/`haven`/`glimmer`, spawn `fromSea`).
 *
 * Spawns: `fromHavenreach` (alias `fromTown`, used by the Havenreach dock) and
 * `start`, `fromHaven`, `fromGlimmer` — each a ship's length off its pier,
 * bow to the open sea; hazard centres `storm_0..1`, `fog_0..1` (radii in
 * SEA_HAZARDS). Buoys float in the harbour channel and drift near hazards.
 */
export function buildSea(): ZoneLayout {
  const rng = new Rng('sea');
  const b = new ZoneBuilder(
    {
      id: 'sea', name: 'The Gleaming Sea', subtitle: 'Between the Sundered Isles', kind: 'sea', biome: 'sea', levelRange: [15, 30], music: 'sea',
      width: 600, depth: 600, seed: 3201, ground: ['sand', 'grass', 'rock', 'dirt'], groundScale: [6, 5, 7, 5], hills: 0, waterLevel: WATER,
      ambient: 'none', mapTint: 0x1e3a4a,
    },
    rng,
  );
  const sheet = b.water[0];
  if (sheet) {
    sheet.deep = false; // the ship sails everywhere there is water; islands block via polygons
    sheet.color = 0x1b4a66;
  }

  const center: V2 = { x: 0, z: 0 };
  const docks = {
    havenreach: dockOf(isle('havenreach'), center, 26),
    haven: dockOf(isle('haven'), center),
    glimmer: dockOf(isle('glimmer'), center),
  };
  const piers = Object.values(docks).map((k) => k.pier);

  const seaFloor = (x: number, z: number): number => -9 + (fbm2(x * 0.012, z * 0.012, 3201, 2) - 0.5) * 4;
  b.addHeight((x, z) => {
    let h = seaFloor(x, z);
    for (const i of SEA_ISLES) {
      const reach = i.r * 1.35 + 20;
      if (Math.abs(x - i.x) > reach || Math.abs(z - i.z) > reach) continue;
      h = Math.max(h, landHeight(i, profile(i), x, z));
    }
    for (const p of piers) h = withPier(h, p, DECK, x, z);
    return h;
  });
  b.addSurface((x, z, w) => {
    let inland = 99;
    for (const i of SEA_ISLES) {
      if (Math.abs(x - i.x) > i.r * 1.4 + 10 || Math.abs(z - i.z) > i.r * 1.4 + 10) continue;
      inland = Math.min(inland, shoreDistance(i, x, z));
    }
    if (inland < -7) w[1] += 3 * Math.min(1, (-inland - 7) / 6);
  });

  // Ships cannot cross land: one polygon per island, a few metres off the beach.
  for (const i of SEA_ISLES) {
    const poly: V2[] = [];
    for (let k = 0; k < 56; k++) {
      const a = (k / 56) * Math.PI * 2;
      const r = shoreRadius(i, a) + 4;
      poly.push({ x: i.x + Math.sin(a) * r, z: i.z + Math.cos(a) * r });
    }
    b.blockers.polys.push(poly);
  }

  // Docks: planked piers (blocked for ships), portal just past each tip.
  const dockPortals: [keyof typeof docks, PortalDef][] = [
    ['havenreach', { id: 'sea_to_havenreach', to: 'havenreach', toSpawn: 'fromSea', x: 0, z: 0, radius: DOCK_RADIUS, label: 'Havenreach Harbour', kind: 'dock' }],
    ['haven', { id: 'sea_to_haven', to: 'haven', toSpawn: 'fromSea', x: 0, z: 0, radius: DOCK_RADIUS, label: 'Haven Isle', kind: 'dock' }],
    ['glimmer', { id: 'sea_to_glimmer', to: 'glimmer', toSpawn: 'fromSea', x: 0, z: 0, radius: DOCK_RADIUS, label: 'Glimmer Isle', kind: 'dock' }],
  ];
  for (const [key, portal] of dockPortals) {
    const dk = docks[key];
    layPier(b, dk.pier, DECK);
    const mid = pierTip(dk.pier, -dk.pier.length / 2);
    b.blockers.rects.push({ x: mid.x, z: mid.z, hw: dk.pier.halfWidth + 0.4, hd: dk.pier.length / 2 + 3, rot: dk.pier.dir });
    b.portal({ ...portal, x: dk.portalAt.x, z: dk.portalAt.z });
    const name = `from${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    b.spawn(name, dk.spawnAt.x, dk.spawnAt.z, dk.pier.dir);
    // Channel buoys guiding ships in.
    const side = { x: Math.cos(dk.pier.dir), z: -Math.sin(dk.pier.dir) };
    for (const t of [18, 42, 66]) {
      const c = pierTip(dk.pier, t);
      for (const s of [-1, 1]) {
        const x = c.x + side.x * 14 * s;
        const z = c.z + side.z * 14 * s;
        b.prop('buoy', x, z, { y: floatY(b, x, z, WATER), rot: t });
      }
    }
  }
  const home = docks.havenreach;
  b.spawn('fromTown', home.spawnAt.x, home.spawnAt.z, home.pier.dir);
  b.spawn('start', home.spawnAt.x, home.spawnAt.z, home.pier.dir);

  // Hazards and drifting debris.
  for (const hz of SEA_HAZARDS) {
    b.spawn(hz.id, hz.x, hz.z, 0);
    for (let k = 0; k < 6; k++) {
      const a = rng.range(0, Math.PI * 2);
      const r = rng.range(0.3, 0.95) * hz.r;
      const x = hz.x + Math.sin(a) * r;
      const z = hz.z + Math.cos(a) * r;
      b.prop(k % 3 === 2 ? 'barrel' : 'buoy', x, z, { y: floatY(b, x, z, WATER, k % 3 === 2 ? -0.35 : 0), rot: a, scale: k % 3 === 2 ? 1.2 : 1 });
    }
  }

  // Island silhouettes seen from the helm.
  const onIsle = (i: Isle, a: number, inland: number): V2 => shorePoint(i, a, -inland);
  const hr = isle('havenreach');
  const hrA = home.pier.dir; // bearing of the harbour pier from the headland centre
  const light = onIsle(hr, hrA + 0.3, 9);
  b.prop('tower', light.x, light.z, { scale: 1.1 });
  for (const [k, [da, d, model]] of ([[-0.18, 18, 'house_a'], [-0.08, 26, 'house_b'], [0.06, 22, 'house_c'], [0.16, 30, 'house_a'], [-0.28, 30, 'house_b'], [0.1, 40, 'house_c']] as const).entries()) {
    const p = onIsle(hr, hrA + da, d);
    b.prop(model, p.x, p.z, { rot: hrA + Math.PI + k * 0.2 });
  }
  for (const da of [-0.4, -0.3, 0.3, 0.42]) {
    const p = onIsle(hr, hrA + da, 34);
    b.prop('wall_segment', p.x, p.z, { rot: hrA + Math.PI / 2 });
  }
  const moor = pierTip(home.pier, -8);
  const ms = { x: moor.x + Math.cos(home.pier.dir) * 6, z: moor.z - Math.sin(home.pier.dir) * 6 };
  b.prop('ship_player', ms.x, ms.z, { rot: home.pier.dir, y: floatY(b, ms.x, ms.z, WATER, -0.9) });

  const hv = isle('haven');
  const manor = onIsle(hv, bearing(hv, center.x, center.z) + Math.PI, 26);
  b.prop('house_a', manor.x, manor.z, { scale: 1.5, rot: bearing(hv, center.x, center.z) });
  const lab = onIsle(hv, bearing(hv, center.x, center.z) + 1.6, 12);
  b.prop('tower', lab.x, lab.z, { scale: 0.8 });

  const gl = isle('glimmer');
  const bloom = onIsle(gl, bearing(gl, center.x, center.z) + Math.PI, 40);
  b.prop('glimmerseed', bloom.x, bloom.z, { scale: 12 });

  // Trees and rocks on every island.
  for (const i of SEA_ISLES) {
    const area = { minX: i.x - i.r * 1.3, minZ: i.z - i.r * 1.3, maxX: i.x + i.r * 1.3, maxZ: i.z + i.r * 1.3 };
    const n = Math.round(i.r * 0.5);
    const palms = scatter(rng, { count: Math.ceil(n * 0.6), ...area, minDist: 6, accept: (p) => {
      const d = shoreDistance(i, p.x, p.z);
      return d < -3 && d > -10;
    } });
    for (const p of palms) b.prop('tree_palm', p.x, p.z, { rot: rng.range(0, 6.28), scale: rng.range(1.0, 1.4) });
    const woods = scatter(rng, { count: i.id === 'havenreach' ? 20 : n, ...area, minDist: 7, accept: (p) => shoreDistance(i, p.x, p.z) < -12 && !b.isReserved(p, 4) });
    for (const p of woods) b.prop(rng.chance(0.7) ? 'tree_oak' : 'tree_pine', p.x, p.z, { rot: rng.range(0, 6.28), scale: rng.range(1.1, 1.6) });
    if (i.id.startsWith('islet')) {
      for (let k = 0; k < 3; k++) {
        const p = onIsle(i, rng.range(0, Math.PI * 2), rng.range(1, i.r * 0.5));
        b.prop('cliff_chunk', p.x, p.z, { rot: rng.range(0, 6.28), scale: rng.range(0.7, 1.3) });
      }
    }
  }

  return b.build();
}
